import React from 'react';
import { FormikField } from 'components/common/form/field/FormikField';
import { Formik, Form } from 'formik';
import { L1ObjectFormData } from './L1ObjectFormModel';
import i18n from 'i18n';
import styles from './l1ObjectForm.module.scss';
import { Link, Redirect } from 'react-router-dom';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import NavigationPrompt from 'components/common/NavigationPrompt/NavigationPrompt';
import _ from 'lodash';
import { L1ObjectBudgetManagement } from './L1ObjectBudgetManagement/L1ObjectBudgetManagement';
import { RTBCampaignGroupForm } from './RTBCampaignGroupForm/RTBCampaignGroupForm';
import { TitleWithBreadcrumbs } from 'components/TitleArea/TitleWithBreadcrumbs';
import { Trans } from 'react-i18next';
import { RtbCampaignListBasic } from 'core/rtbCampaign/RtbCampaign';
import { getFieldErrors } from 'utils/FormikUtils';
import { getCurrencyLabel } from 'helper/CurrencyHelper';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

// ECOPD-5630: 活動群組的 CBO 開關要不要出現。
// RTB 內建 CBO —— 它是 static 渠道，registry 裡的 addonFeatures 恆為 {}，
// 只讀渠道旗標會讓它靜默失去現有的 CBO；動態渠道（PIC Outdoor 等）才由投遞渠道的
// 「開放CBO功能」（addonFeatures.campaign.supportCBO）決定。
export const isCboAllowedOnChannel = (channel: string): boolean => {
  const { config } = ChannelGroupSetting.fromKey(channel);
  return (
    ChannelGroupSetting.isCboBuiltInChannelGroup(config.id) ||
    _.get(config, 'addonFeatures.campaign.supportCBO', false)
  );
};

export const L1ObjectForm: React.FC<{
  useModel: () => L1ObjectFormData;
}> = (props) => {
  const modelData: L1ObjectFormData = props.useModel();
  if (!modelData.initL1Object) {
    return <LoadingIndicator />;
  }

  return (
    <div className={styles.l1ObjectForm}>
      <NavigationPrompt when={!modelData.redirectData} />
      {modelData.loading && <LoadingIndicator />}
      {modelData.redirectData && <Redirect to={modelData.redirectData} />}
      <div className={styles.titleArea}>
        <TitleWithBreadcrumbs
          title={modelData.title}
          routes={modelData.breadcrumbs}
        />
      </div>
      <Formik
        initialValues={modelData.initL1Object}
        enableReinitialize
        onSubmit={modelData.save}
        validate={modelData.validate}
        validateOnBlur={false}
      >
        {({ values, errors, touched, submitCount, setValues }) => {
          const isCBO = _.get(values, 'autoOptimise', false);
          const getBudgetTips = () => {
            if (getFieldErrors({ errors, touched, submitCount }, 'budget')) {
              return;
            }
            if (!modelData.showRemainBudget(values)) {
              return;
            }
            const currentBudget = _.defaultTo(values.budget, 0).toString();
            return (
              <Trans
                i18nKey='campaign.descriptions.remainBudget'
                values={{
                  currency: getCurrencyLabel(modelData.order.currency),
                  remainBudget: modelData.totalBudget - +currentBudget
                }}
                components={{ 1: <span className='text-dark' /> }}
              />
            );
          };
          const cboHint = modelData.getCboChangeHint(values);
          const onEditBudgetBtnClick = () => {
            modelData.onCBOChange(values, setValues, false);
          };

          const channelFormatter = (value) => {
            const option = modelData.l1ObjectTypeOptions.find(
              (option) => option.value === value
            );
            return option?.label || value;
          };
          const renderForm = () => (
            <RTBCampaignGroupForm
              formType={modelData.formType}
              isCBO={isCBO}
              canEditBidStrategy={modelData.canEditBidStrategy}
              channel={values.channel}
              currency={modelData.order.currency}
              campaigns={modelData.l2ObjectList as RtbCampaignListBasic[]}
              campaignBudgets={_.get(values, 'rtb.campaign_budgets')}
              objectiveOptions={modelData.objectiveOptions}
              onEditCampaignBudgetsBtn={onEditBudgetBtnClick}
              getBudgetTips={getBudgetTips}
              canEditViewableImpressInReportSetting={
                modelData.canEditViewableImpressInReportSetting
              }
            />
          );

          return (
            <>
              <Form>
                <div className={styles.formContent}>
                  {modelData.budgetManagementModalData && (
                    <L1ObjectBudgetManagement
                      {...modelData.budgetManagementModalData}
                    />
                  )}
                  <FormikField.Input
                    label={i18n.t('l1Object.labels.name')}
                    name='name'
                  />
                  {modelData.formType === 'create' ? (
                    <FormikField.Select
                      label={i18n.t('l1Object.labels.channel')}
                      name='channel'
                      simpleValue
                      options={modelData.l1ObjectTypeOptions}
                      className={styles.selectField}
                      onChange={_.partial(modelData.onChannelChange, setValues)}
                    />
                  ) : (
                    <FormikField.Label
                      label={i18n.t('l1Object.labels.channel')}
                      name='channel'
                      formatter={channelFormatter}
                    />
                  )}
                  {values.channel && isCboAllowedOnChannel(values.channel) && (
                    <FormikField.Switch
                      label={i18n.t('l1Object.labels.autoOptimise')}
                      name='autoOptimise'
                      onChange={_.partial(
                        modelData.onCBOChange,
                        values,
                        setValues
                      )}
                      disabled={!!cboHint}
                      hint={cboHint}
                    />
                  )}
                  {renderForm()}
                </div>
                <div className={styles.formButtons}>
                  <button type='submit' className='btn btn-primary btn-sm'>
                    {i18n.t('common.buttons.submit')}
                  </button>
                  <Link
                    to={`${modelData.cancelTargetPath}`}
                    className='btn btn-secondary btn-sm'
                  >
                    {i18n.t('common.buttons.cancel')}
                  </Link>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};
